import { useEffect, useRef } from "react";
import { sendGTMEvent } from "@next/third-parties/google";

export function usePopularRentalCarAnalytics({
  summaryData,
  items = [],
  locationType = null,
}) {
  const viewedItems = useRef(new Set());

  const resetViewedItems = () => {
    setTimeout(() => {
      viewedItems.current = new Set();
    }, 3000);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const elements = document.querySelectorAll("[data-car-item]");
    if (!elements || elements.length === 0) return;

    const cleanups = [];

    elements.forEach((el) => {
      const indexAttr = el.getAttribute("data-car-item-index");

      const index = Number.isNaN(Number(indexAttr)) ? 0 : Number(indexAttr);
      const item = items[index];

      if (!item) return;

      const itemKey = item.Reference || index;

      const cleanup = observeCarListItem(el, {
        once: true,
        onVisible: () => {
          if (viewedItems.current.has(itemKey)) {
            return;
          }

          viewedItems.current.add(itemKey);

          sendGTMEvent({
            event: "custom_event",
            event_category: "recommendation",
            event_action: "view_recommendation",
            recommendation_id: [item?.Brand || "", item?.Model || ""].join(" "),
            recommendation_name: "car",
            recommendation_brand: item?.Company?.Name,
            recommendation_category: "popular_rental_car",
            recommendation_type: locationType,
            start_date: null,
            end_date: null,
          });
        },
      });

      if (typeof cleanup === "function") {
        cleanups.push(cleanup);
      }
    });

    return () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch (e) {
          // no-op
        }
      });
    };
  }, [items, summaryData]);

  const trackSelectItem = (item) => {
    sendGTMEvent({
      event: "custom_event",
      event_category: "recommendation",
      event_action: "select_recommendation",
      recommendation_id: [item?.Brand || "", item?.Model || ""].join(" "),
      recommendation_name: "car",
      recommendation_brand: item?.Company?.Name,
      recommendation_category: "popular_rental_car",
      recommendation_type: locationType,
      start_date: null,
      end_date: null,
    });
  };

  return {
    trackSelectItem,
    resetViewedItems,
  };
}

export function observeCarListItem(
  element,
  { once = true, onVisible, observerOptions } = {},
) {
  if (!element || typeof IntersectionObserver === "undefined") {
    return () => {};
  }

  let hasFired = false;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry || !entry.isIntersecting) return;

      if (once && hasFired) return;
      hasFired = true;

      if (typeof onVisible === "function") {
        onVisible(entry);
      }

      if (once) {
        observer.unobserve(entry.target);
      }
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0.25,
      ...(observerOptions || {}),
    },
  );

  observer.observe(element);

  // Cleanup
  return () => {
    try {
      observer.disconnect();
    } catch (e) {
      // no-op
    }
  };
}
